import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./usersList.module.css";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import Modal from "../modals/modal/Modal";
import PrivacyProtectionPopup from "../modals/privacyProtectionPopup/privacyProtectionPopup";
import TreatmentPlan from "../treatmentPlan/treatmentPlan";
import { IUserReport } from "../../services/types/users.types";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import PermanentDeleteConfirmation from "../modals/permanentDeleteConfirmation/permanentDeleteConfirmation";
import {
  useLazyGetUsersByCompanyIdWithRecommendsQuery,
  usePermanentDeleteClientUserMutation,
  useUpdateUserStatusMutation,
} from "../../services/api/user.api";
import { useGetCompanyByIdQuery, useCreatePaymentPageMutation, useCreateBulkPaymentJobMutation } from "../../services/api/utils.api";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { isSuperAdminByRoleId } from "../../services/selectors/admin.selectors";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import ListPagination from "../listPagination/listPagination";
import ActionButtonTooltip from "../ActionButtonTooltip/actionButtonTooltip";
import checkedIcon from "../../images/checked.png";
import editProfileIcon from "../../images/edit_profile_icon.png";
import deleteIcon from "../../images/delete_icon.svg";
import StatusSwitchToggle from "../../components/statusSwitchToggle/statusSwitchToggle";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useActions } from "../../services/hooks/useActions";
import { currentUserSelector } from "../../services/selectors/users.selectors";
import PaymentActionConfirmModal from "../modals/paymentActionConfirmModal/paymentActionConfirmModal";
import BulkSendPaymentHistoryModal from "../modals/bulkSendPaymentHistoryModal/bulkSendPaymentHistoryModal";
import UserDebtModal from "../modals/userDebtModal/userDebtModal";
import SingleUserTokenModal, { SingleUserTokenConfirmData } from "../modals/singleUserTokenModal/singleUserTokenModal";
import PaymentLinkDisplayModal from "../modals/paymentLinkDisplayModal/paymentLinkDisplayModal";
import { PaymentOperation, ICreatePaymentPageRequest, ICreateBulkPaymentJobRequest, ICreateBulkPaymentJobResponse } from "../../services/types/utils.types";

function UsersList({
  userList,
  openUserActionsMenu,
  openEditUserForm,
  shouldResetPagination,
  setShouldResetLimit,
  selectedUserId,
  fromFinancialDashboard,
  refetchUsersTable,
}: {
  userList: IUserReport[];
  openUserActionsMenu: Dispatch<SetStateAction<IUserReport | undefined>>;
  openEditUserForm: (user: IUserReport, initialTab?: 'payments' | 'therapists') => void;
  shouldResetPagination: boolean;
  setShouldResetLimit: (bool: boolean) => void;
  selectedUserId?: number;
  fromFinancialDashboard?: boolean;
  /** Reloads the table with the filters the page currently has active. */
  refetchUsersTable?: () => void;
}) {
  const admin = useAppSelector(adminSelector);
  const isSuperAdmin = useAppSelector(isSuperAdminByRoleId);
  const { data: currentCompany } = useGetCompanyByIdQuery(admin.companyId, {
    skip: !admin.companyId,
  });
  const isChargingCustomers = currentCompany?.isChargingCustomers ?? false;

  const [createPaymentPage] = useCreatePaymentPageMutation();
  const [createBulkPaymentJob] = useCreateBulkPaymentJobMutation();
  const navigate = useNavigate();

  const [user, setUser] = useState<IUserReport | undefined>(undefined);
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [showTreatmentPlan, setShowTreatmentPlan] = useState(false);
  const [openTreatmentPlanModal, setOpenTreatmentPlanModal] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openPermanentDeleteConfirmation, setOpenPermanentDeleteConfirmation] =
    useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<{ text: string; wide?: boolean } | undefined>(
    undefined
  );
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [paymentActionModal, setPaymentActionModal] = useState<{
    isOpen: boolean;
    operation: PaymentOperation | null;
  }>({ isOpen: false, operation: null });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [debtModalUser, setDebtModalUser] = useState<IUserReport | null>(null);
  const [pendingEditTab, setPendingEditTab] = useState<'payments' | 'therapists'>('therapists');
  // Single user token flow states
  const [singleUserTokenModal, setSingleUserTokenModal] = useState<{
    isOpen: boolean;
    user: IUserReport | null;
    operation: PaymentOperation | null;
  }>({ isOpen: false, user: null, operation: null });
  const [paymentLinkModal, setPaymentLinkModal] = useState<{
    isOpen: boolean;
    url: string;
    userName: string;
  }>({ isOpen: false, url: '', userName: '' });
  const [createdPaymentJob, setCreatedPaymentJob] = useState<ICreateBulkPaymentJobResponse | null>(null);
  const [bulkSendHistoryModalOpen, setBulkSendHistoryModalOpen] = useState(false);
  const limitNumber = 9;

  //params to store current page index of the table
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page");
  const pageAsNumber = Number(page);

  const [usersLimits, setUsersLimits] = useState(
    pageAsNumber
      ? {
        start: (pageAsNumber - 1) * limitNumber + 1,
        end: pageAsNumber * limitNumber,
      }
      : { start: 1, end: limitNumber }
  );

  const [pendingAction, setPendingAction] = useState<"none" | "permanentDelete">(
    "none"
  );
  /** Status the toggle is about to apply (1 = activate, 0 = deactivate). */
  const [pendingStatus, setPendingStatus] = useState<number | null>(null);

  //highlight user
  const { setCurrentUser } = useActions();
  const selectUser = (id: number) => () => {
    setCurrentUser(id);
  };
  const currentUser = useAppSelector(currentUserSelector);

  // Handle selectedUserId from financial dashboard
  useEffect(() => {
    if (selectedUserId && fromFinancialDashboard && userList.length > 0) {
      // Find the user in the list
      const userIndex = userList.findIndex(u => u.Users.Id === selectedUserId);

      if (userIndex !== -1) {
        // Calculate which page the user is on
        const userPage = Math.ceil((userIndex + 1) / limitNumber);
        const newStart = (userPage - 1) * limitNumber + 1;
        const newEnd = userPage * limitNumber;

        // Update pagination to show the correct page
        setUsersLimits({ start: newStart, end: newEnd });
        setSearchParams({ page: userPage.toString() });

        // Set as current user to highlight
        setCurrentUser(selectedUserId);

        // Scroll to the user row after a short delay to ensure DOM is ready
        setTimeout(() => {
          const userRow = document.querySelector(`[data-user-id="${selectedUserId}"]`);
          if (userRow) {
            userRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [selectedUserId, fromFinancialDashboard, userList, setCurrentUser, limitNumber, setSearchParams]);

  // User selection handlers for charging customers
  const handleUserSelection = (userId: number, isSelected: boolean) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const isUserSelected = (userId: number) => selectedUserIds.has(userId);

  // Select all functionality - selects all filtered users, not just visible ones
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      // Select all users in the filtered list
      const allUserIds = new Set(userList.map(u => u.Users.Id));
      setSelectedUserIds(allUserIds);
    } else {
      // Deselect all
      setSelectedUserIds(new Set());
    }
  };

  // Check if all filtered users are selected
  const areAllSelected = userList.length > 0 && userList.every(u => selectedUserIds.has(u.Users.Id));
  const areSomeSelected = userList.some(u => selectedUserIds.has(u.Users.Id)) && !areAllSelected;

  // Action button handlers - opens confirmation modal
  const handleCreateToken = () => {
    // For single user selection, show the special modal with delivery options
    if (selectedUserIds.size === 1) {
      const userId = Array.from(selectedUserIds)[0];
      const userReport = userList.find(u => u.Users.Id === userId);
      if (userReport) {
        setSingleUserTokenModal({ isOpen: true, user: userReport, operation: 'CreateToken' });
        return;
      }
    }
    // For multiple users, use the standard confirmation modal
    setPaymentActionModal({ isOpen: true, operation: 'CreateToken' });
  };

  const handleChargeCustomer = () => {
    // For single user selection, show the special modal with delivery options
    if (selectedUserIds.size === 1) {
      const userId = Array.from(selectedUserIds)[0];
      const userReport = userList.find(u => u.Users.Id === userId);
      if (userReport) {
        setSingleUserTokenModal({ isOpen: true, user: userReport, operation: 'ChargeOnly' });
        return;
      }
    }
    // For multiple users, use the standard confirmation modal
    setPaymentActionModal({ isOpen: true, operation: 'ChargeOnly' });
  };

  const handleCreateTokenAndCharge = () => {
    // For single user selection, show the special modal with delivery options
    if (selectedUserIds.size === 1) {
      const userId = Array.from(selectedUserIds)[0];
      const userReport = userList.find(u => u.Users.Id === userId);
      if (userReport) {
        setSingleUserTokenModal({ isOpen: true, user: userReport, operation: 'ChargeAndToken' });
        return;
      }
    }
    // For multiple users, use the standard confirmation modal
    setPaymentActionModal({ isOpen: true, operation: 'ChargeAndToken' });
  };

  const closePaymentActionModal = () => {
    setPaymentActionModal({ isOpen: false, operation: null });
  };

  const closeSingleUserTokenModal = () => {
    setSingleUserTokenModal({ isOpen: false, user: null, operation: null });
  };

  const closePaymentLinkModal = () => {
    setPaymentLinkModal({ isOpen: false, url: '', userName: '' });
  };

  // Process single user token creation with delivery method choice
  const processSingleUserToken = async (data: SingleUserTokenConfirmData) => {
    const { deliveryMethod, invoiceUserName, maxNumOfPayments, customMessage } = data;
    const user = singleUserTokenModal.user;
    if (!user) return;

    setIsProcessingPayment(true);

    try {
      // For CreateToken operation, skip if user already has token
      // For ChargeOnly and ChargeAndToken operations, we create payment link regardless
      const operation = singleUserTokenModal.operation || 'CreateToken';
      const hasToken = user.HasPaymentMethod ?? false;

      if (operation === 'CreateToken' && hasToken) {
        // User already has token - skip for CreateToken only
        setIsProcessingPayment(false);
        closeSingleUserTokenModal();
        setOpenErrorModal({ text: t('USER_ALREADY_HAS_TOKEN') });
        setSelectedUserIds(new Set());
        return;
      }

      // Balance used for skip rules; server derives amount/items on GetPaymentPage
      const userBalance = user.Balance ?? 0;
      const balanceDebt = userBalance < 0 ? Math.abs(userBalance) : 0;

      const request: ICreatePaymentPageRequest = {
        UsersGuid: user.Users.Guid,
        SlikaType: 1, // PaymentPage
        Operation: operation,
        DocumentType: (operation === 'ChargeOnly' || operation === 'ChargeAndToken') ? 1 : null as unknown as number,
        // Set SMS/Email flags based on delivery method
        IsSendLinkSms: deliveryMethod === 'sendToUser',
        IsSendLinkMail: deliveryMethod === 'sendToUser',
        // Include invoice/installment options when provided (ChargeOnly and ChargeAndToken)
        ...(invoiceUserName ? { InvoiceUserName: invoiceUserName } : {}),
        ...(maxNumOfPayments != null ? { MaxNumOfPayments: maxNumOfPayments } : {}),
        // Include custom message if provided
        ...(customMessage ? { CustomMessage: customMessage } : {}),
      };

      const result = await createPaymentPage(request).unwrap();
      const responseCode = result.ResponseCode ?? result.responseCode;
      const description = result.Description ?? result.description;
      const paymentUrl = result.Url;

      if (responseCode === 0) {
        closeSingleUserTokenModal();
        setSelectedUserIds(new Set());

        if (deliveryMethod === 'displayHere' && paymentUrl) {
          // Show the payment link modal
          const userName = user.Users.FullName ||
            `${user.Users.FirstName} ${user.Users.LastName || ''}`.trim();
          setPaymentLinkModal({ isOpen: true, url: paymentUrl, userName });
        } else {
          // Link sent to user - show success
          setOpenSuccessModal(true);
        }
      } else {
        closeSingleUserTokenModal();
        setOpenErrorModal({ text: description || t('UNKNOWN_ERROR') });
      }
    } catch (error: any) {
      closeSingleUserTokenModal();
      setOpenErrorModal({
        text: error?.data?.message || error?.message || t('UNKNOWN_ERROR')
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Process payment for all selected users
  const processPaymentForSelectedUsers = async (customMessage?: string) => {
    if (!paymentActionModal.operation || selectedUserIds.size === 0) return;

    setIsProcessingPayment(true);
    const operation = paymentActionModal.operation;
    const userIdsArray = Array.from(selectedUserIds);
    if (operation === "ChargeToken") {
      setIsProcessingPayment(false);
      closePaymentActionModal();
      setOpenErrorModal({ text: t("PAYMENT_OPERATION_FAILED") });
      return;
    }

    try {
      const request: ICreateBulkPaymentJobRequest = {
        ClientIds: userIdsArray,
        CompanyId: admin.companyId,
        Operation: operation,
        IsSendLinkSms: true,
        IsSendLinkMail: true,
        DocumentType: (operation === "ChargeOnly" || operation === "ChargeAndToken") ? 1 : null,
        ...(customMessage ? { CustomMessage: customMessage } : {}),
      };

      const result = await createBulkPaymentJob(request).unwrap();
      closePaymentActionModal();
      setSelectedUserIds(new Set());
      setCreatedPaymentJob(result);
    } catch (error: any) {
      closePaymentActionModal();
      const message = error?.data?.message || error?.message;
      setOpenErrorModal({
        text: message === "jobs_schema_missing" ? t("JOBS_SCHEMA_MISSING") : message || t("PAYMENT_OPERATION_FAILED"),
        wide: true,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  useEffect(() => {
    // reset pagination on each input change
    if (shouldResetPagination) {
      setUsersLimits({ start: 1, end: Math.max(limitNumber, 1) });
      setShouldResetLimit(false);
    } else if (
      pageAsNumber * limitNumber > userList.length &&
      userList.length > 0
    )
      setUsersLimits({
        start:
          Math.floor((userList.length - 1) / limitNumber) * limitNumber + 1,
        end: userList.length,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userList]);

  useEffect(
    () => {
      setSearchParams({
        page: (Math.floor(usersLimits.start / limitNumber) + 1).toString(),
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersLimits]
  );

  const proceed = () => {
    if (pendingAction === "permanentDelete" && user) {
      setOpenPermanentDeleteConfirmation(true);
      setPendingAction("none");
      setOpenPrivacyPopup(false);
      return;
    }

    if (showTreatmentPlan) {
      setOpenPrivacyPopup(false);
      setOpenTreatmentPlanModal(true);
      return;
    }

    if (user && showEditUserForm) {
      openEditUserForm(user, pendingEditTab);
      setPendingEditTab('therapists');
    } else {
      openUserActionsMenu(user);
    }
  };

  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [permanentDeleteClientUser] = usePermanentDeleteClientUserMutation();
  const [getAllUsersByCompanyIdWithRecommends] =
    useLazyGetUsersByCompanyIdWithRecommendsQuery();

  const handlePermanentDeleteUser = ({
    deleteClinicalData,
  }: {
    deleteClinicalData: boolean;
  }) => {
    if (!user) return;
    const { Password, Roles, Id, ...rest } = user.Users;

    permanentDeleteClientUser({
      companyId: admin.companyId,
      // @ts-ignore — server expects same PascalCase user payload as DML/2
      body: { ...rest, deleteClinicalData },
    }).then((res) => {
      if ("data" in res && res.data) {
        if ("desc" in res.data && typeof res.data?.desc === "string") {
          setOpenErrorModal({ text: res.data.desc });
        } else {
          setOpenSuccessModal(true);
        }
      } else if ("error" in res) {
        setOpenErrorModal({ text: t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER") });
      }
    });
  };

  // Status changes go through the dedicated PATCH user/{guid}/status endpoint — it carries the
  // status and nothing else, and the fulfilled response patches the row in the users slice.
  // `target` is passed when the row is toggled directly (activation), since the `user` state
  // is not committed yet at that point.
  const handleChangeUserStatus = (newStatusId: number, target?: IUserReport) => {
    const guid = (target ?? user)?.Users.Guid;
    if (!guid) {
      setOpenErrorModal({ text: t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER") });
      return;
    }

    updateUserStatus({ guid: String(guid), statusId: newStatusId }).then((res) => {
      if ("data" in res && res.data?.success) {
        setOpenSuccessModal(true);
      } else if ("data" in res && res.data === null) {
        // Plan/quota rejection — base-query already showed the restriction popup.
      } else {
        const desc = (res as any)?.error?.data?.error;
        setOpenErrorModal({
          text:
            typeof desc === "string"
              ? desc
              : t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"),
        });
      }
    });
  };

  return (
    <div className={styles.usersListContainer}>
      <article className={`section ${styles.section}`}>
        <h3 className="sectionTitle">{t("USER_CLIENTS")}</h3>


        <ul className="list">
          <div className={styles.tableHeader}>

            {/* Payment Action Buttons - only show if company charges customers */}
            {isChargingCustomers && (
              <div className={styles.paymentActionsBar}>
                <button
                  className={styles.paymentActionButton}
                  onClick={handleCreateToken}
                  disabled={selectedUserIds.size === 0}
                >
                  {t('PAYMENT_TOKEN_CREATION_LINK')}
                </button>
                <button
                  className={styles.paymentActionButton}
                  onClick={handleChargeCustomer}
                  disabled={selectedUserIds.size === 0}
                >
                  {t('CHARGE_CUSTOMER_LINK')}
                </button>
                <button
                  className={styles.paymentActionButton}
                  onClick={handleCreateTokenAndCharge}
                  disabled={selectedUserIds.size === 0}
                >
                  {t('CREATE_TOKEN_AND_CHARGE_LINK')}
                </button>
                <button
                  className={styles.paymentActionButton}
                  onClick={() => setBulkSendHistoryModalOpen(true)}
                  disabled={selectedUserIds.size === 0}
                >
                  {t('BULK_SEND_PAYMENT_HISTORY_TITLE')}
                </button>
                {selectedUserIds.size > 0 && (
                  <span className={styles.selectedCount}>
                    {t('SELECTED_USERS', { count: selectedUserIds.size })}
                  </span>
                )}
                <div className={styles.legendContainer}>
                  <span className={styles.legendIcon} tabIndex={0}>
                    ℹ
                  </span>
                  <div className={styles.legendTooltip}>
                    <p><strong>{t('PAYMENT_TOKEN_CREATION_LINK')}:</strong> {t('LEGEND_PAYMENT_TOKEN_CREATION_LINK')}</p>
                    <p><strong>{t('CHARGE_CUSTOMER_LINK')}:</strong> {t('LEGEND_CHARGE_CUSTOMER_LINK')}</p>
                    <p><strong>{t('CREATE_TOKEN_AND_CHARGE_LINK')}:</strong> {t('LEGEND_CREATE_TOKEN_AND_CHARGE_LINK')}</p>
                    <p><strong>{t('BULK_SEND_PAYMENT_HISTORY_TITLE')}:</strong> {t('LEGEND_BULK_SEND_PAYMENT_HISTORY')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <li className={`listRow listRow_header ${styles.user} ${isChargingCustomers ? styles.userWithBalance : ''}`}>
            {isChargingCustomers && (
              <p className={`listCell ${styles.checkboxCell}`}>
                <input
                  type="checkbox"
                  checked={areAllSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = areSomeSelected;
                    }
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className={styles.userCheckbox}
                  title={t('SELECT_ALL_FILTERED')}
                />
              </p>
            )}
            <p className={`listCell`} title={t("ACTIONS")}>{t("ACTIONS")}</p>
            <p className={`listCell`} title={t("COLUMN_ID")}>{t("COLUMN_ID")}</p>
            <p className={`listCell`} title={t("NAME_2")}>{t("NAME_2")}</p>
            <p className={`listCell`} title={t("PHONE")}>{t("PHONE")}</p>
            <p className={`listCell`} title={t("TOTAL_REPORTS")}>{t("TOTAL_REPORTS")}</p>
            <p className={`listCell`} title={t("RECOMMENDATION")}>{t("RECOMMENDATION")}</p>
            <p className={`listCell`} title={t("TREATMENT_PLANS")}>{t("TREATMENT_PLANS")}</p>
            <p className={`listCell`} title={t("EDIT")}>{t("EDIT")}</p>
            {isChargingCustomers && <p className={`listCell`} title={t("BALANCE")}>{t("BALANCE")}</p>}
            {isChargingCustomers && <p className={`listCell`} title={t("PAYMENT_METHOD_HEADER")}>{t("PAYMENT_METHOD_HEADER")}</p>}
            <p className={`listCell`} title={t("COLUMN_STATUS")}>{t("COLUMN_STATUS")}</p>
          </li>
          {userList?.length ? (
            userList.map(
              (u: IUserReport, index: number) =>
                index >= usersLimits.start - 1 &&
                index < usersLimits.end && (
                  // !mobile ? (
                  <li
                    key={index}
                    data-testid={`users-client-row-${index}`}
                    data-user-id={u.Users.Id}
                    className={`listRow ${styles.user} ${isChargingCustomers ? styles.userWithBalance : ''} ${index % 2 === 0 && "colored"
                      } ${u.Users.Id === currentUser ? "listRow_selected" : ""}`}
                    onClick={selectUser(u.Users.Id)}
                  >
                    {isChargingCustomers && (
                      <p className={`listCell ${styles.checkboxCell}`}>
                        <input
                          type="checkbox"
                          checked={isUserSelected(u.Users.Id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleUserSelection(u.Users.Id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={styles.userCheckbox}
                          title={t('SELECT_USER')}
                        />
                      </p>
                    )}
                    <p className={`listCell`}>
                      {u.Users.RoleId === -2 ? (
                        <span
                          className={styles.contactBadge}
                          data-tooltip={t("CONTACT_PERSON_TOOLTIP")}
                        >
                          👤
                        </span>
                      ) : (
                        <button
                          type={"button"}
                          data-testid="user-actions-button"
                          className={`${styles.btn} ${styles.btn_type_actions}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setUser(u);
                            setOpenPrivacyPopup(true);
                          }}
                        />
                      )}
                    </p>
                    <p className={`listCell`}>{u.Users.Id}</p>
                    <p className={`listCell`} data-testid="client-name">
                      {u.Users.FullName ? (
                        u.Users.FullName
                      ) : (
                        <>
                          {u.Users.FirstName} {u.Users.LastName || ""}
                        </>
                      )}
                    </p>
                    <p className={`listCell`}>{u.Users.Phone || "-"}</p>
                    <p className={`listCell`}>{u.TotalReports || "-"}</p>
                    <p className={`${`listCell`} ${styles.recommendation}`}>
                      {u.Recommends || "-"}
                    </p>
                    <div className="listCell edit_icon">

                      <ActionButtonTooltip
                        className={`${styles.btn} ${styles.btn_type_treatmentPlan}`}
                        onClick={() => {
                          setUser(u);
                          setShowTreatmentPlan(true);
                          setOpenPrivacyPopup(true);
                        }}
                        text={t("OPEN_TREATMENT_PLANS")}
                        tooltipPosition="center"
                      >
                        {(u.AppointmentsProgramsNum ?? 0) > 0 ? (
                          <img
                            src={checkedIcon}
                            alt={t("checked icon")}
                            className="iconImage"
                          />
                        ) : '-'}
                      </ActionButtonTooltip>
                    </div>
                    <div className="listCell edit_icon">
                      <ActionButtonTooltip
                        data-testid="edit-client-button"
                        className={`btn ${styles.btn_type_edit}`}
                        onClick={() => {
                          setUser(u);
                          setShowEditUserForm(true);
                          setOpenPrivacyPopup(true);
                        }}
                        text={t("EDIT_USER")}
                        tooltipPosition="center"
                      >
                        <img
                          src={editProfileIcon}
                          alt={t("EDIT_1")}
                          className="editIconImage"
                        />
                      </ActionButtonTooltip>
                    </div>
                    {isChargingCustomers && (
                      <p
                        className={`listCell ${styles.balanceCell} ${styles.balanceClickable} ${(u.Balance ?? 0) > 0 ? styles.balancePositive :
                          (u.Balance ?? 0) < 0 ? styles.balanceNegative : ''
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDebtModalUser(u);
                        }}
                        title={t("CLICK_TO_VIEW_DEBT_DETAILS")}
                      >
                        {u.Balance ?? 0}
                      </p>
                    )}
                    {isChargingCustomers && (
                      <div className={`listCell ${styles.paymentMethodCell}`}>
                        <div className={styles.paymentIndicators}>
                          {u.HasPaymentMethod && (
                            <>
                              <span
                                className={styles.paymentMethodIcon}
                                title={t("HAS_PAYMENT_METHOD")}
                              >
                                💳
                              </span>
                              {u.PaymentCollectionMethodId === 1 && (
                                <span
                                  className={`${styles.collectionMethodIcon} ${styles.appCollection}`}
                                  title={t("APP_PAYMENT_COLLECTION")}
                                >
                                  💻
                                </span>
                              )}
                            </>
                          )}
                          {u.PaymentCollectionMethodId === 2 && (
                            <span
                              className={`${styles.collectionMethodIcon} ${styles.manualCollection}`}
                              title={t("MANUAL_PAYMENT_COLLECTION")}
                            >
                              🚫
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={`listCell ${styles.statusCell}`}>
                      <span className={styles.statusCellToggleWrap}>
                        <StatusSwitchToggle
                          isActive={u.Users.StatusId === 1}
                          onToggle={() => {
                            // No privacy declaration — a status change exposes no clinical data.
                            // Deactivation asks for confirmation; activation is harmless and
                            // reversible, so it applies straight away.
                            setUser(u);
                            if (u.Users.StatusId === 1) {
                              setPendingStatus(0);
                              setOpenDeleteConfirmation(true);
                            } else {
                              handleChangeUserStatus(1, u);
                            }
                          }}
                        />
                      </span>
                      {isSuperAdmin && (
                        <>
                          <span
                            className={styles.statusCellDivider}
                            aria-hidden
                          />
                          <span
                            className={styles.permanentDeleteWrap}
                            data-testid="permanent-delete-client-button"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ActionButtonTooltip
                              className={styles.btn_permanentDelete}
                              onClick={() => {
                                setUser(u);
                                setPendingAction("permanentDelete");
                                setOpenPrivacyPopup(true);
                              }}
                              text={t("PERMANENT_DELETE_USER_TOOLTIP")}
                              tooltipPosition="center"
                            >
                              <img
                                src={deleteIcon}
                                alt=""
                                className={styles.permanentDeleteIcon}
                              />
                            </ActionButtonTooltip>
                          </span>
                        </>
                      )}
                    </div>
                  </li>
                )
            )
          ) : (
            <p className={`text`}>{t("NO_USERS_FOUND")}</p>
          )}
        </ul>
        {openDeleteConfirmation && pendingStatus !== null && (
          <Modal
            onClose={() => {
              setOpenDeleteConfirmation(false);
              setPendingStatus(null);
            }}
          >
            <DeleteConfirmation
              onDelete={() => handleChangeUserStatus(pendingStatus)}
              text={t("ARE_YOU_SURE_TO_CHANGE_STATUS", {
                firstName: user?.Users?.FirstName,
              })}
              onClose={() => {
                setOpenDeleteConfirmation(false);
                setPendingStatus(null);
              }}
            />
          </Modal>
        )}

        {openPermanentDeleteConfirmation && user && (
          <Modal
            wide
            onClose={() => {
              setOpenPermanentDeleteConfirmation(false);
              setUser(undefined);
            }}
          >
            <PermanentDeleteConfirmation
              user={user}
              onDelete={handlePermanentDeleteUser}
              onClose={() => {
                setOpenPermanentDeleteConfirmation(false);
                setUser(undefined);
              }}
            />
          </Modal>
        )}

        {openPrivacyPopup && user && (
          <Modal
            onClose={() => {
              setOpenPrivacyPopup(false);
              setUser(undefined);
              setOpenDeleteConfirmation(false);
              setOpenPermanentDeleteConfirmation(false);
              setPendingAction("none");
              setShowEditUserForm(false);
              setShowTreatmentPlan(false);
            }}
          >
            <PrivacyProtectionPopup
              onClose={proceed}
              clientUsersGuid={user?.Users.Guid || ""}
            />
          </Modal>
        )}
        {openTreatmentPlanModal && user && (
          <Modal
            wide
            onClose={() => {
              setOpenTreatmentPlanModal(false);
              setUser(undefined);
              setShowTreatmentPlan(false);
            }}
          >
            <TreatmentPlan
              user={user}
              inModal
              onClose={() => {
                setOpenTreatmentPlanModal(false);
                setUser(undefined);
                setShowTreatmentPlan(false);
              }}
            />
          </Modal>
        )}
        {openSuccessModal && (
          <Modal
            onClose={() => {
              setOpenSuccessModal(false);
              refetchUsersTable?.();
            }}
          >
            <GeneralSuccessPopup
              onClose={() => {
                setOpenSuccessModal(false);
                refetchUsersTable?.();
              }}
            />
          </Modal>
        )}
        {openErrorModal && (
          <Modal
            onClose={() => {
              setOpenErrorModal(undefined);
            }}
            wide={openErrorModal.wide}
          >
            <ErrorPopup
              text={openErrorModal.text}
              onClose={() => {
                setOpenErrorModal(undefined);
              }}
            />
          </Modal>
        )}
        {paymentActionModal.isOpen && paymentActionModal.operation && (
          <Modal onClose={closePaymentActionModal}>
            <PaymentActionConfirmModal
              onClose={closePaymentActionModal}
              onConfirm={processPaymentForSelectedUsers}
              operation={paymentActionModal.operation}
              selectedCount={selectedUserIds.size}
              isProcessing={isProcessingPayment}
            />
          </Modal>
        )}
        {debtModalUser && (
          <Modal onClose={() => setDebtModalUser(null)} wide>
            <UserDebtModal
              onClose={() => setDebtModalUser(null)}
              userGuid={debtModalUser.Users.Guid || ""}
              userName={
                debtModalUser.Users.FullName ||
                `${debtModalUser.Users.FirstName} ${debtModalUser.Users.LastName || ""}`.trim()
              }
              userId={debtModalUser.Users.Id}
              onPaymentSuccess={() => {
                getAllUsersByCompanyIdWithRecommends({
                  companyIds: [admin.companyId],
                  roleIds: [1],
                  fakeCompanyIds: [admin?.fakeCompanyId || -1],
                });
              }}
              onViewPaymentHistory={() => {
                const u = debtModalUser;
                setDebtModalUser(null);
                setPendingEditTab('payments');
                setUser(u);
                setShowEditUserForm(true);
                setOpenPrivacyPopup(true);
              }}
            />
          </Modal>
        )}
        {singleUserTokenModal.isOpen && singleUserTokenModal.user && (
          <Modal onClose={closeSingleUserTokenModal}>
            <SingleUserTokenModal
              onClose={closeSingleUserTokenModal}
              onConfirm={processSingleUserToken}
              userName={
                singleUserTokenModal.user.Users.FullName ||
                `${singleUserTokenModal.user.Users.FirstName} ${singleUserTokenModal.user.Users.LastName || ''}`.trim()
              }
              isProcessing={isProcessingPayment}
              operation={singleUserTokenModal.operation || undefined}
            />
          </Modal>
        )}
        {paymentLinkModal.isOpen && (
          <Modal onClose={closePaymentLinkModal}>
            <PaymentLinkDisplayModal
              onClose={closePaymentLinkModal}
              paymentUrl={paymentLinkModal.url}
              userName={paymentLinkModal.userName}
            />
          </Modal>
        )}
        {createdPaymentJob && (
          <Modal onClose={() => setCreatedPaymentJob(null)}>
            <article className={styles.jobCreatedPopup} data-testid="payment-job-created-popup">
              <h2 className={styles.jobCreatedTitle}>{t("PAYMENT_JOB_CREATED")}</h2>
              <p className={styles.jobCreatedText}>
                {t("PAYMENT_JOB_CREATED_INFO", { count: createdPaymentJob.totalItems })}
              </p>
              <div className={styles.jobCreatedButtons}>
                <button
                  type="button"
                  className={`button hover ${styles.jobCreatedPrimaryButton}`}
                  onClick={() => {
                    const jobId = createdPaymentJob.jobId;
                    setCreatedPaymentJob(null);
                    navigate(`/jobs/${jobId}`);
                  }}
                >
                  {t("VIEW_JOB")}
                </button>
                <button
                  type="button"
                  className={`button hover ${styles.jobCreatedSecondaryButton}`}
                  onClick={() => setCreatedPaymentJob(null)}
                >
                  {t("CLOSE_PANEL")}
                </button>
              </div>
            </article>
          </Modal>
        )}
        {bulkSendHistoryModalOpen && (
          <Modal onClose={() => setBulkSendHistoryModalOpen(false)} wide>
            <BulkSendPaymentHistoryModal
              onClose={() => setBulkSendHistoryModalOpen(false)}
              selectedUserIds={selectedUserIds}
              userList={userList}
              companyId={admin.companyId}
            />
          </Modal>
        )}
      </article>
      {userList && userList.length > 0 && (
        <ListPagination
          limits={usersLimits}
          total={userList?.length || 0}
          setLimits={setUsersLimits}
          step={limitNumber}
        />
      )}
    </div>
  );
}

export default UsersList;
